// TrashView — 휴지통 화면 (스마트폴더 ?trash=1).
//
// 2026-10-03: 이전엔 휴지통 폴더가 일반 목록을 보여줘서 "삭제해도 휴지통에 없다"로 보였다.
// - 삭제된 항목 목록 (GET /api/items?trash=1)
// - 항목별 복원 (POST /restore) · 영구 삭제 (DELETE /permanent, 확인)
// - 휴지통 비우기 (전체 영구 삭제, 확인) — 개별 실패를 격리(allSettled)하고 결과를 알린다
// - 30일 지난 항목은 서버 cron(purge-trash)이 자동 삭제

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RotateCcw, Trash2 } from 'lucide-react';
import { itemsApi, type ItemMeta } from '../api/items';
import { ITEMS_LIST_KEY } from '../hooks/useItems';
import { formatApiError } from '../api/client';
import { useToast } from './Toast';
import { Spinner } from './Spinner';
import { TYPE_LABEL } from './DetailPanel/typeMap';
import type { ItemType } from '../api/items';

const TRASH_FILTER = { trash: true } as const;
const RETENTION_DAYS = 30;

/** 삭제 시각 → "N일 후 자동 삭제" */
function remainingLabel(deletedAt: number | null | undefined): string {
  if (!deletedAt) return '';
  const left = Math.ceil((deletedAt + RETENTION_DAYS * 86_400_000 - Date.now()) / 86_400_000);
  return left > 0 ? `${left}일 후 자동 삭제` : '곧 자동 삭제';
}

export function TrashView() {
  const qc = useQueryClient();
  const toast = useToast();
  const [busyId, setBusyId] = useState<number | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ITEMS_LIST_KEY(TRASH_FILTER),
    queryFn: () => itemsApi.listItems(TRASH_FILTER),
  });
  const items: ItemMeta[] = data?.items ?? [];

  // 목록 전체(휴지통·활성) 갱신
  const refresh = () => qc.invalidateQueries({ queryKey: ['items'] });

  const restore = useMutation({
    mutationFn: (id: number) => itemsApi.restoreItem(id),
    onSuccess: () => { toast.success('복원했습니다'); void refresh(); },
    onError: (err) => toast.error(formatApiError(err, '복원 실패')),
    onSettled: () => setBusyId(null),
  });

  const purge = useMutation({
    mutationFn: (id: number) => itemsApi.purgeItem(id),
    onSuccess: () => { toast.success('영구 삭제했습니다'); void refresh(); },
    onError: (err) => toast.error(formatApiError(err, '영구 삭제 실패')),
    onSettled: () => setBusyId(null),
  });

  const emptyAll = useMutation({
    mutationFn: async (ids: number[]) => {
      // 개별 실패 격리 — 일부 실패해도 나머지는 진행
      const results = await Promise.allSettled(ids.map((id) => itemsApi.purgeItem(id)));
      return results.filter((r) => r.status === 'rejected').length;
    },
    onSuccess: (failed, ids) => {
      if (failed === 0) toast.success(`휴지통을 비웠습니다 (${ids.length}개)`);
      else toast.error(`${ids.length - failed}개 삭제, ${failed}개 실패 — 다시 시도해 주세요`);
      void refresh();
    },
    onError: (err) => toast.error(formatApiError(err, '휴지통 비우기 실패')),
  });

  const handlePurge = (item: ItemMeta) => {
    if (!window.confirm(`"${item.name}"을(를) 영구 삭제할까요?\n되돌릴 수 없습니다.`)) return;
    setBusyId(item.id);
    purge.mutate(item.id);
  };

  const handleEmpty = () => {
    if (!window.confirm(`휴지통의 항목 ${items.length}개를 모두 영구 삭제할까요?\n되돌릴 수 없습니다.`)) return;
    emptyAll.mutate(items.map((i) => i.id));
  };

  return (
    <section className="explorer-section trash-view" aria-label="휴지통">
      <header className="explorer-section__header">
        <span className="explorer-section__icon" aria-hidden="true"><Trash2 size={15} /></span>
        <h3 className="explorer-section__title">휴지통</h3>
        <span className="explorer-section__count">{items.length}</span>
        <span className="trash-view__hint">삭제 후 {RETENTION_DAYS}일이 지나면 자동으로 영구 삭제됩니다</span>
        {items.length > 0 && (
          <button
            type="button"
            className="btn btn--sm btn--danger trash-view__empty-btn"
            onClick={handleEmpty}
            disabled={emptyAll.isPending}
          >
            {emptyAll.isPending ? '비우는 중…' : '휴지통 비우기'}
          </button>
        )}
      </header>

      {isLoading ? (
        <Spinner />
      ) : isError ? (
        <p className="muted">휴지통을 불러오지 못했습니다.</p>
      ) : items.length === 0 ? (
        <p className="muted trash-view__empty">휴지통이 비어 있습니다.</p>
      ) : (
        <ul className="trash-view__list">
          {items.map((item) => (
            <li key={item.id} className="trash-view__row">
              <div className="trash-view__info">
                <span className="trash-view__name">{item.name}</span>
                <span className="badge badge--type">{TYPE_LABEL[item.type as ItemType] ?? item.type}</span>
                <span className="trash-view__meta">{remainingLabel(item.deleted_at)}</span>
              </div>
              <div className="trash-view__actions">
                <button
                  type="button"
                  className="btn btn--sm btn--ghost"
                  onClick={() => { setBusyId(item.id); restore.mutate(item.id); }}
                  disabled={busyId === item.id}
                  aria-label={`${item.name} 복원`}
                >
                  <RotateCcw size={13} aria-hidden="true" /> 복원
                </button>
                <button
                  type="button"
                  className="btn btn--sm btn--danger"
                  onClick={() => handlePurge(item)}
                  disabled={busyId === item.id}
                  aria-label={`${item.name} 영구 삭제`}
                >
                  영구 삭제
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
